import React, { useState } from 'react';
import { 
  Shield, Store, ShoppingBag, Database, Bot, Globe, 
  CreditCard, Brain, Bell, Warehouse, FileText, MessageSquare,
  ArrowRight, ArrowDown, Users, Package, BarChart3, Zap,
  Lock, RefreshCw, Eye, ChevronDown, ChevronUp
} from 'lucide-react';

const FlowArrow = ({ direction = 'right', label, color = '#f97316' }) => (
  <div className={`flex ${direction === 'down' ? 'flex-col items-center' : 'flex-row items-center'} gap-1`}>
    <div className="h-0.5 w-8 rounded" style={{ backgroundColor: color }} />
    {label && <span className="text-[10px] text-gray-500 whitespace-nowrap">{label}</span>}
    {direction === 'right' ? <ArrowRight className="w-3 h-3" style={{ color }} /> : <ArrowDown className="w-3 h-3" style={{ color }} />}
  </div>
);

const SystemNode = ({ icon: Icon, title, subtitle, color, items, expanded, onToggle, badge }) => (
  <div 
    className="rounded-xl border p-3 cursor-pointer transition-all hover:scale-[1.02]"
    style={{ borderColor: color + '40', backgroundColor: color + '08' }}
    onClick={onToggle}
  >
    <div className="flex items-start justify-between">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg" style={{ backgroundColor: color + '20' }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{title}</p>
          <p className="text-[10px] text-gray-400">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {badge && (
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ backgroundColor: color + '30', color }}>
            {badge}
          </span>
        )}
        {items && (expanded ? <ChevronUp className="w-3 h-3 text-gray-500" /> : <ChevronDown className="w-3 h-3 text-gray-500" />)}
      </div>
    </div>
    {expanded && items && (
      <div className="mt-2 pl-8 space-y-1 border-t border-gray-800 pt-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.status === 'ok' ? '#22c55e' : item.status === 'mock' ? '#f59e0b' : '#ef4444' }} />
            <span className="text-[10px] text-gray-300">{item.text}</span>
            <span className="text-[9px] ml-auto" style={{ color: item.status === 'ok' ? '#22c55e' : item.status === 'mock' ? '#f59e0b' : '#ef4444' }}>
              {item.status === 'ok' ? 'ATIVO' : item.status === 'mock' ? 'MOCK' : 'FALTA'}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const ArchitectureDiagram = () => {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto" data-testid="architecture-diagram">
      {/* Header */}
      <div className="text-center mb-8">
        <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-14 h-14 mx-auto mb-3 object-contain" />
        <h1 className="text-3xl font-bold text-white mb-2">Arquitetura do Sistema ClickHub</h1>
        <p className="text-gray-400 text-sm">Diagrama completo de fluxos, modulos e integrações - 178 endpoints</p>
        <div className="flex justify-center gap-4 mt-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
            <span className="text-xs text-gray-400">Funcionando</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            <span className="text-xs text-gray-400">Mockado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span className="text-xs text-gray-400">Falta Implementar</span>
          </div>
        </div>
      </div>

      {/* CAMADA 1: ENTRADA */}
      <div className="rounded-2xl border border-gray-800 bg-gray-900/50 p-4">
        <h2 className="text-sm font-bold text-orange-400 mb-3 flex items-center gap-2">
          <Lock className="w-4 h-4" /> CAMADA 1 - ENTRADA (Autenticacao JWT)
        </h2>
        <div className="grid grid-cols-4 gap-3">
          <SystemNode
            icon={Lock}
            title="Login / Registro"
            subtitle="POST /api/auth/login"
            color="#f97316"
            badge="JWT"
            expanded={expanded.auth}
            onToggle={() => toggle('auth')}
            items={[
              { text: 'Login com email/senha', status: 'ok' },
              { text: 'Registro novo usuario', status: 'ok' },
              { text: 'Refresh token (7 dias)', status: 'ok' },
              { text: 'Logout', status: 'ok' },
              { text: 'Recuperar senha por email', status: 'falta' },
              { text: 'Verificacao de email', status: 'falta' },
            ]}
          />
          <SystemNode
            icon={Shield}
            title="Admin"
            subtitle="Acesso total ao sistema"
            color="#ef4444"
            badge="ROLE"
            expanded={expanded.admin}
            onToggle={() => toggle('admin')}
            items={[
              { text: 'Dashboard com metricas', status: 'mock' },
              { text: 'Gerenciar usuarios', status: 'ok' },
              { text: 'Painel do Robo', status: 'ok' },
              { text: 'Configurar margens', status: 'ok' },
              { text: 'Autorizar repasses', status: 'mock' },
              { text: 'Todos os pedidos', status: 'ok' },
            ]}
          />
          <SystemNode
            icon={Store}
            title="Fornecedor"
            subtitle="Cadastra produtos, gerencia estoque"
            color="#3b82f6"
            badge="ROLE"
            expanded={expanded.forn}
            onToggle={() => toggle('forn')}
            items={[
              { text: 'Meu Painel', status: 'ok' },
              { text: 'Cadastrar produtos', status: 'ok' },
              { text: 'Importar catalogo IA', status: 'ok' },
              { text: 'WMS / Picking', status: 'ok' },
              { text: 'Nota Fiscal', status: 'mock' },
              { text: 'Receber repasses', status: 'mock' },
            ]}
          />
          <SystemNode
            icon={ShoppingBag}
            title="Afiliado"
            subtitle="Vende nos marketplaces"
            color="#22c55e"
            badge="ROLE"
            expanded={expanded.afil}
            onToggle={() => toggle('afil')}
            items={[
              { text: 'Meu Painel', status: 'ok' },
              { text: 'Vincular produtos', status: 'ok' },
              { text: 'Conectar marketplaces', status: 'ok' },
              { text: 'Carteira digital', status: 'ok' },
              { text: 'Depositar via PIX', status: 'ok' },
              { text: 'Comissoes automaticas', status: 'mock' },
            ]}
          />
        </div>
      </div>

      {/* CAMADA 2: CORE */}
      <div className="grid grid-cols-3 gap-4">
        {/* Coluna 1: Produtos */}
        <div className="rounded-2xl border border-gray-800 bg-gray-900/50 p-4 space-y-3">
          <h2 className="text-sm font-bold text-blue-400 mb-1 flex items-center gap-2">
            <Package className="w-4 h-4" /> PRODUTOS E CATALOGO
          </h2>
          <SystemNode
            icon={Package}
            title="Catalogo de Produtos"
            subtitle="8 produtos / Filtros / Busca"
            color="#8b5cf6"
            badge="8"
            expanded={expanded.cat}
            onToggle={() => toggle('cat')}
            items={[
              { text: 'GET /api/produtos', status: 'ok' },
              { text: 'POST /api/fornecedor/{id}/produto', status: 'ok' },
              { text: 'Filtro por categoria', status: 'ok' },
              { text: 'Vincular produto', status: 'ok' },
              { text: 'Ver detalhes', status: 'ok' },
            ]}
          />
          <SystemNode
            icon={Brain}
            title="Importacao com IA"
            subtitle="Upload CSV/Excel + GPT-4o"
            color="#ec4899"
            badge="IA"
            expanded={expanded.import}
            onToggle={() => toggle('import')}
            items={[
              { text: 'Upload de catalogo', status: 'ok' },
              { text: 'Enriquecimento com IA', status: 'ok' },
              { text: 'Gerar titulo/descricao', status: 'ok' },
              { text: 'Reprocessar produto', status: 'ok' },
            ]}
          />
          <SystemNode
            icon={Warehouse}
            title="WMS - Armazem"
            subtitle="Estoque / Picking / Expedicao"
            color="#14b8a6"
            badge="WMS"
            expanded={expanded.wms}
            onToggle={() => toggle('wms')}
            items={[
              { text: 'Produtos no armazem', status: 'ok' },
              { text: 'Picking por barcode', status: 'ok' },
              { text: 'Expedicao de pedidos', status: 'ok' },
              { text: 'Ajuste de inventario', status: 'ok' },
              { text: 'Logs e estatisticas', status: 'ok' },
            ]}
          />
        </div>

        {/* Coluna 2: Motor Central */}
        <div className="rounded-2xl border border-orange-800/40 bg-orange-900/10 p-4 space-y-3">
          <h2 className="text-sm font-bold text-orange-400 mb-1 flex items-center gap-2">
            <Zap className="w-4 h-4" /> MOTOR CENTRAL
          </h2>
          <SystemNode
            icon={Bot}
            title="Robo de Postagem"
            subtitle="Publica automaticamente"
            color="#f97316"
            badge="CORE"
            expanded={expanded.robo}
            onToggle={() => toggle('robo')}
            items={[
              { text: 'Painel de controle ON/OFF', status: 'ok' },
              { text: 'Fila de publicacoes', status: 'ok' },
              { text: 'Logs em tempo real', status: 'ok' },
              { text: 'Alertas e erros', status: 'ok' },
              { text: 'POST real no ML', status: 'falta' },
              { text: 'POST real na Shopee', status: 'falta' },
              { text: 'Captura de vendas webhook', status: 'mock' },
            ]}
          />
          <SystemNode
            icon={Brain}
            title="IA de Anuncios"
            subtitle="GPT-4o gera titulo, descricao, tags"
            color="#a855f7"
            badge="GPT-4o"
            expanded={expanded.ia}
            onToggle={() => toggle('ia')}
            items={[
              { text: 'Gerar anuncio completo', status: 'ok' },
              { text: 'Analise de imagem', status: 'ok' },
              { text: 'Posts para redes sociais', status: 'ok' },
              { text: 'Pipeline 5 etapas', status: 'ok' },
            ]}
          />
          <SystemNode
            icon={BarChart3}
            title="ERP / Business"
            subtitle="Pedidos / Comissoes / Financeiro"
            color="#06b6d4"
            badge="ERP"
            expanded={expanded.erp}
            onToggle={() => toggle('erp')}
            items={[
              { text: 'Criar pedidos', status: 'ok' },
              { text: 'Calcular comissao', status: 'ok' },
              { text: 'Calcular margem', status: 'ok' },
              { text: 'Repasses a fornecedor', status: 'mock' },
              { text: 'Notas fiscais', status: 'mock' },
              { text: 'Contas pagar/receber', status: 'mock' },
            ]}
          />
        </div>

        {/* Coluna 3: Integrações Externas */}
        <div className="rounded-2xl border border-gray-800 bg-gray-900/50 p-4 space-y-3">
          <h2 className="text-sm font-bold text-green-400 mb-1 flex items-center gap-2">
            <Globe className="w-4 h-4" /> INTEGRACOES EXTERNAS
          </h2>
          <SystemNode
            icon={Globe}
            title="Marketplaces (OAuth 2.0)"
            subtitle="14 plataformas conectaveis"
            color="#22c55e"
            badge="14"
            expanded={expanded.oauth}
            onToggle={() => toggle('oauth')}
            items={[
              { text: 'Mercado Livre (testado)', status: 'ok' },
              { text: 'Shopee', status: 'falta' },
              { text: 'Amazon Brasil', status: 'falta' },
              { text: 'Magazine Luiza', status: 'falta' },
              { text: 'Shein', status: 'falta' },
              { text: 'Casas Bahia', status: 'falta' },
              { text: 'Outros 8 marketplaces', status: 'falta' },
            ]}
          />
          <SystemNode
            icon={CreditCard}
            title="PIX (Mercado Pago)"
            subtitle="Deposito via QR Code"
            color="#eab308"
            badge="SANDBOX"
            expanded={expanded.pix}
            onToggle={() => toggle('pix')}
            items={[
              { text: 'Gerar QR Code PIX', status: 'ok' },
              { text: 'Webhook confirmacao', status: 'ok' },
              { text: 'Simular pagamento', status: 'ok' },
              { text: 'Chaves de PRODUCAO', status: 'falta' },
            ]}
          />
          <SystemNode
            icon={Bell}
            title="Push Notifications"
            subtitle="Web Push API + VAPID"
            color="#f472b6"
            badge="PUSH"
            expanded={expanded.push}
            onToggle={() => toggle('push')}
            items={[
              { text: 'Subscribe/Unsubscribe', status: 'ok' },
              { text: 'Notificar vendas', status: 'ok' },
              { text: 'Notificar pedidos WMS', status: 'ok' },
              { text: 'Teste de notificacao', status: 'ok' },
            ]}
          />
          <SystemNode
            icon={MessageSquare}
            title="Chat IA (Suporte)"
            subtitle="GPT-4o-mini assistente"
            color="#6366f1"
            badge="IA"
            expanded={expanded.chat}
            onToggle={() => toggle('chat')}
            items={[
              { text: 'Multiplas conversas', status: 'ok' },
              { text: 'Historico persistente', status: 'ok' },
              { text: 'Nova conversa', status: 'ok' },
            ]}
          />
        </div>
      </div>

      {/* CAMADA 3: BANCO DE DADOS */}
      <div className="rounded-2xl border border-gray-800 bg-gray-900/50 p-4">
        <h2 className="text-sm font-bold text-cyan-400 mb-3 flex items-center gap-2">
          <Database className="w-4 h-4" /> CAMADA DE DADOS (MongoDB)
        </h2>
        <div className="grid grid-cols-6 gap-2">
          {[
            { name: 'usuarios', count: '3', color: '#f97316' },
            { name: 'produtos', count: '8', color: '#8b5cf6' },
            { name: 'pedidos', count: '-', color: '#3b82f6' },
            { name: 'comissoes', count: '-', color: '#22c55e' },
            { name: 'carteiras', count: '-', color: '#eab308' },
            { name: 'oauth_tokens', count: '1', color: '#14b8a6' },
            { name: 'afiliados', count: '-', color: '#22c55e' },
            { name: 'fornecedores', count: '-', color: '#3b82f6' },
            { name: 'conversations', count: '7+', color: '#6366f1' },
            { name: 'wms_products', count: '-', color: '#14b8a6' },
            { name: 'notifications', count: '-', color: '#f472b6' },
            { name: 'catalogo_imp', count: '-', color: '#ec4899' },
          ].map((col) => (
            <div key={col.name} className="rounded-lg border border-gray-800 p-2 text-center bg-gray-950/50">
              <Database className="w-3 h-3 mx-auto mb-1" style={{ color: col.color }} />
              <p className="text-[10px] text-gray-300 font-medium">{col.name}</p>
              <p className="text-xs font-bold" style={{ color: col.color }}>{col.count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FLUXO PRINCIPAL */}
      <div className="rounded-2xl border border-orange-800/30 bg-orange-900/5 p-4">
        <h2 className="text-sm font-bold text-orange-400 mb-4 flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> FLUXO PRINCIPAL DE OPERACAO
        </h2>
        <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
          {[
            { step: '1', text: 'Fornecedor\ncadastra produto', icon: Store, color: '#3b82f6' },
            { step: '2', text: 'Afiliado\nvincula produto', icon: ShoppingBag, color: '#22c55e' },
            { step: '3', text: 'Afiliado conecta\nmarketplace OAuth', icon: Globe, color: '#14b8a6' },
            { step: '4', text: 'IA gera anuncio\nautomatico', icon: Brain, color: '#a855f7' },
            { step: '5', text: 'Robo publica\nno marketplace', icon: Bot, color: '#f97316' },
            { step: '6', text: 'Venda acontece\nwebhook recebido', icon: CreditCard, color: '#eab308' },
            { step: '7', text: 'Comissao\ncreditada', icon: BarChart3, color: '#22c55e' },
            { step: '8', text: 'Repasse ao\nfornecedor', icon: Store, color: '#3b82f6' },
          ].map((s, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center text-center min-w-[100px]">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mb-1" style={{ backgroundColor: s.color + '20', border: `2px solid ${s.color}` }}>
                  <s.icon className="w-4 h-4" style={{ color: s.color }} />
                </div>
                <span className="text-[9px] font-bold" style={{ color: s.color }}>PASSO {s.step}</span>
                <span className="text-[10px] text-gray-400 whitespace-pre-line leading-tight mt-0.5">{s.text}</span>
              </div>
              {i < 7 && <ArrowRight className="w-4 h-4 text-gray-600 flex-shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* STATUS GERAL */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-xl border border-green-800/30 bg-green-900/10 p-4">
          <h3 className="text-sm font-bold text-green-400 mb-2">Funcionando (12)</h3>
          <div className="space-y-1">
            {['Auth JWT', 'Login 3 roles', 'IA Anuncios GPT-4o', 'IA por Imagem', 'Chat Suporte IA', 'OAuth ML', 'PIX Sandbox', 'WMS Picking', 'Push Notifications', 'Catalogo Import', 'Perfis Usuario', 'Sidebar por Role'].map(t => (
              <div key={t} className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span className="text-[10px] text-gray-300">{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-yellow-800/30 bg-yellow-900/10 p-4">
          <h3 className="text-sm font-bold text-yellow-400 mb-2">Mockado (5)</h3>
          <div className="space-y-1">
            {['Dashboard Admin dados', 'Robo nao posta real', 'WhatsApp placeholders', 'Nota Fiscal mock', 'Contabilidade mock'].map(t => (
              <div key={t} className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                <span className="text-[10px] text-gray-300">{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-red-800/30 bg-red-900/10 p-4">
          <h3 className="text-sm font-bold text-red-400 mb-2">Falta Implementar (8)</h3>
          <div className="space-y-1">
            {['POST real Mercado Livre', 'OAuth 13 marketplaces', 'PIX producao', 'JWT_SECRET fixo', 'Rate Limiting', 'Verificacao email', 'HTTPS / Dominio', 'Backup MongoDB'].map(t => (
              <div key={t} className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span className="text-[10px] text-gray-300">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="flex justify-center gap-6 pt-2 pb-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-orange-400">178</p>
          <p className="text-[10px] text-gray-500">Endpoints API</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-400">14</p>
          <p className="text-[10px] text-gray-500">Marketplaces</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-400">12</p>
          <p className="text-[10px] text-gray-500">Colecoes MongoDB</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-400">3</p>
          <p className="text-[10px] text-gray-500">Roles de Usuario</p>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureDiagram;
